/**
 * CORE-773 — the frozen-snapshot capability guard, shared by the web room
 * (`LiveRoomScreen`) and the native room VM (`useLiveRoom`).
 *
 * Both surfaces read per-cap rights off the session's FROZEN capability snapshot
 * (`room.capabilities`) exactly the same way, and both had this guard inlined
 * byte-for-byte. Two copies of a back-compat rule is a drift hazard: if one side
 * were "simplified" to a naive `capabilities.includes(cap)`, functions would
 * silently close on that platform for existing users during a rolling backend
 * rollout — the exact failure the App parity ticket must not ship. One helper,
 * one behaviour.
 *
 * The rule this pins (the rolling-rollout / back-compat AC of CORE-767):
 *
 *  - The frozen snapshot is honoured ONLY when the unified-live flag is ON *and*
 *    the room actually carries a non-empty snapshot. A backend that predates the
 *    capability field ships `room.capabilities` as `null`/`undefined` (and an
 *    empty `[]` is treated the same — "no snapshot", not "everything revoked").
 *
 *  - In that fallback path every cap resolves to the caller-supplied `legacy`
 *    default, so a legacy/old-backend room keeps its pre-unification gating
 *    byte-identical: a host stays a host, base host actions stay open. The
 *    snapshot can only ever *narrow* rights below the legacy default for rooms
 *    that actually carry one — it never collapses an unknown room to empty.
 *
 * Never derive a right from the level or `isMaster` here — this reads the frozen
 * snapshot only (prohibitions #1 and #2 of the ticket).
 */
export function resolveFrozenCap(
  unifiedLive: boolean,
  sessionCaps: readonly string[] | null | undefined,
  cap: string,
  legacy: boolean,
): boolean {
  const useFrozenCaps = unifiedLive && Array.isArray(sessionCaps) && sessionCaps.length > 0;
  return useFrozenCaps ? sessionCaps!.includes(cap) : legacy;
}
